import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import MobileBottomNav from '../../components/ui/MobileBottomNav';
import Button from '../../components/ui/Button';
import CalendarView from './components/CalendarView';
import EventCard from './components/EventCard';
import EventDetailsModal from './components/EventDetailsModal';
import CreateEventModal from './components/CreateEventModal';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';


const EventsCalendar = () => {
  const navigate = useNavigate();
  const { user, userProfile, currentTenant, isGymOwner } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    if (currentTenant?.id) {
      loadEvents();
      loadRegistrations();
    }
  }, [currentTenant]);

  const loadEvents = async () => {
    try {
      const { data, error } = await supabase
        ?.from('events')
        ?.select('*, organizer:organizer_id(full_name, avatar_url)')
        ?.eq('tenant_id', currentTenant?.id)
        ?.order('start_time', { ascending: true });

      if (error) throw error;
      setEvents(data || []);
    } catch (error) {
      console.error('Error loading events:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadRegistrations = async () => {
    try {
      const { data, error } = await supabase
        ?.from('event_registrations')
        ?.select('*')
        ?.eq('user_id', user?.id);

      if (error) throw error;
      setRegistrations(data || []);
    } catch (error) {
      console.error('Error loading registrations:', error);
    }
  };

  const handleRegister = async (eventId) => {
    try {
      const event = events?.find(e => e?.id === eventId);
      if (!event) return;

      const { error } = await supabase
        ?.from('event_registrations')
        ?.insert({
          event_id: eventId,
          user_id: user?.id,
          status: 'registered'
        });

      if (error) throw error;

      await loadRegistrations();
      alert(`Successfully registered for ${event?.title}!`);
    } catch (error) {
      console.error('Error registering:', error);
      alert('Failed to register. Please try again.');
    }
  };

  const handleCancelRegistration = async (eventId) => {
    try {
      const { error } = await supabase
        ?.from('event_registrations')
        ?.delete()
        ?.eq('event_id', eventId)
        ?.eq('user_id', user?.id);

      if (error) throw error;

      await loadRegistrations();
      alert('Registration cancelled successfully.');
    } catch (error) {
      console.error('Error cancelling registration:', error);
      alert('Failed to cancel registration.');
    }
  };

  const handleCreateEvent = async (eventData) => {
    try {
      const { error } = await supabase
        ?.from('events')
        ?.insert({
          ...eventData,
          tenant_id: currentTenant?.id,
          organizer_id: user?.id
        });

      if (error) throw error;

      await loadEvents();
      setShowCreateModal(false);
      alert('Event created successfully!');
    } catch (error) {
      console.error('Error creating event:', error);
      alert('Failed to create event.');
    }
  };

  const filteredEvents = events?.filter(event => {
    if (filter === 'all') return true;
    if (filter === 'registered') {
      return registrations?.some(r => r?.event_id === event?.id);
    }
    return event?.event_type === filter;
  });

  const upcomingEvents = filteredEvents?.filter(e => new Date(e?.start_time) >= new Date());

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-6 pb-24 md:pb-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Events Calendar
            </h1>
            <p className="text-muted-foreground">
              Discover and register for upcoming gym events
            </p>
          </div>
          {isGymOwner && (
            <Button
              onClick={() => setShowCreateModal(true)}
              iconName="Plus"
              iconPosition="left"
            >
              Create Event
            </Button>
          )}
        </div>

        <div className="mb-6">
          <CalendarView
            events={events}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            registrations={registrations}
          />
        </div>

        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'registered', 'workshop', 'seminar', 'competition', 'social', 'special']?.map(type => (
            <Button
              key={type}
              variant={filter === type ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilter(type)}
              className="whitespace-nowrap"
            >
              {type?.charAt(0)?.toUpperCase() + type?.slice(1)}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {upcomingEvents?.map(event => (
            <EventCard
              key={event?.id}
              event={event}
              isRegistered={registrations?.some(r => r?.event_id === event?.id)}
              onRegister={handleRegister}
              onCancel={handleCancelRegistration}
              onViewDetails={() => setSelectedEvent(event)}
            />
          ))}
        </div>

        {upcomingEvents?.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Calendar" size={64} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Events Found
            </h3>
            <p className="text-muted-foreground">
              {filter === 'all' ? 'No upcoming events scheduled.' : `No ${filter} events found.`}
            </p>
          </div>
        )}
      </main>
      <MobileBottomNav />
      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          isRegistered={registrations?.some(r => r?.event_id === selectedEvent?.id)}
          onClose={() => setSelectedEvent(null)}
          onRegister={handleRegister}
          onCancel={handleCancelRegistration}
        />
      )}
      {showCreateModal && (
        <CreateEventModal
          onClose={() => setShowCreateModal(false)}
          onCreate={handleCreateEvent}
        />
      )}
    </div>
  );
};

export default EventsCalendar;
